import {_RequestEnvelope} from '../core/types';
import {
    AuthMethodPreparationError,
    AuthMethodStatus,
    AuthMethodType,
    AuthPreset,
    FirebaseError,
    FirebaseWebApp,
    GoogleViaFirebaseManagedNotReady,
    GoogleViaFirebaseManagedStatus,
} from '../models/authconfig';
import {RealmInfo} from '../models/config';

// ---------------------------------------------------------------------------
// CanSetGoogleViaFirebaseManaged
// ---------------------------------------------------------------------------

export interface CanSetGoogleViaFirebaseManagedRequest {
    AuthPresetId: string;
    ProjectId: string;
    AppId: string | null;
    ServiceAccountKeyJson: string;
}

export const CanSetGoogleViaFirebaseManagedResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AuthPresetNotFound: 'AuthPresetNotFound',
    AuthPresetDeleted: 'AuthPresetDeleted',
    MethodPolicyError: 'MethodPolicyError',
    WebAppNotFound: 'WebAppNotFound',
    FirebaseError: 'FirebaseError',
} as const;
export type CanSetGoogleViaFirebaseManagedResult =
    typeof CanSetGoogleViaFirebaseManagedResultCode[keyof typeof CanSetGoogleViaFirebaseManagedResultCode];

export type CanSetGoogleViaFirebaseManagedResponse =
    | {
    Result: 'Ok' | 'MethodPolicyError' | 'FirebaseError';
    ResultDescription: string;
    ManagedStatus: GoogleViaFirebaseManagedStatus;
    WebApps: FirebaseWebApp[];
    MethodPolicyError: string;
    FirebaseError: FirebaseError;
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound' | 'AuthPresetDeleted' | 'WebAppNotFound';
    ResultDescription: string;
    ManagedStatus: null;
    WebApps: null;
    MethodPolicyError: null;
    FirebaseError: null;
};

const _canSetGoogleViaFirebaseManagedMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AuthPresetNotFound',
    3: 'AuthPresetDeleted',
    4: 'MethodPolicyError',
    5: 'WebAppNotFound',
    6: 'FirebaseError',
};

export function canSetGoogleViaFirebaseManaged(
    params: CanSetGoogleViaFirebaseManagedRequest
): _RequestEnvelope<CanSetGoogleViaFirebaseManagedResponse> {
    return {...params, _endpoint: 'admin/authConfig/canSetGoogleViaFirebaseManaged', _resultCodeMap: _canSetGoogleViaFirebaseManagedMap};
}

// ---------------------------------------------------------------------------
// CheckAuthPreset
// ---------------------------------------------------------------------------

export interface CheckAuthPresetRequest {
    AuthPresetId: string;
}

export const CheckAuthPresetResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AuthPresetNotFound: 'AuthPresetNotFound',
} as const;
export type CheckAuthPresetResult =
    typeof CheckAuthPresetResultCode[keyof typeof CheckAuthPresetResultCode];

export type CheckAuthPresetResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Statuses: AuthMethodStatus[];
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound';
    ResultDescription: string;
    Statuses: null;
};

const _checkAuthPresetMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AuthPresetNotFound',
};

export function checkAuthPreset(
    params: CheckAuthPresetRequest
): _RequestEnvelope<CheckAuthPresetResponse> {
    return {...params, _endpoint: 'admin/authConfig/checkAuthPreset', _resultCodeMap: _checkAuthPresetMap};
}

// ---------------------------------------------------------------------------
// CheckRealmAuth
// ---------------------------------------------------------------------------

export interface CheckRealmAuthRequest {
    RealmId: string;
}

export const CheckRealmAuthResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NoAuthPreset: 'NoAuthPreset',
    RealmNotFound: 'RealmNotFound',
} as const;
export type CheckRealmAuthResult =
    typeof CheckRealmAuthResultCode[keyof typeof CheckRealmAuthResultCode];

export type CheckRealmAuthResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Statuses: AuthMethodStatus[];
}
    | {
    Result: 'Unknown' | 'NoAuthPreset' | 'RealmNotFound';
    ResultDescription: string;
    Statuses: null;
};

const _checkRealmAuthMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NoAuthPreset',
    3: 'RealmNotFound',
};

export function checkRealmAuth(
    params: CheckRealmAuthRequest
): _RequestEnvelope<CheckRealmAuthResponse> {
    return {...params, _endpoint: 'admin/authConfig/checkRealmAuth', _resultCodeMap: _checkRealmAuthMap};
}

// ---------------------------------------------------------------------------
// CloneAuthPresetForRealm
// ---------------------------------------------------------------------------

export interface CloneAuthPresetForRealmRequest {
    RealmId: string;
    NewPresetName: string | null;
    NewPresetDescription: string | null;
}

export const CloneAuthPresetForRealmResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    RealmNotFound: 'RealmNotFound',
    NoAuthPreset: 'NoAuthPreset',
    RealmPresetChanged: 'RealmPresetChanged',
} as const;
export type CloneAuthPresetForRealmResult =
    typeof CloneAuthPresetForRealmResultCode[keyof typeof CloneAuthPresetForRealmResultCode];

export type CloneAuthPresetForRealmResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'RealmNotFound' | 'NoAuthPreset' | 'RealmPresetChanged';
    ResultDescription: string;
    AuthPreset: null;
};

const _cloneAuthPresetForRealmMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'RealmNotFound',
    3: 'NoAuthPreset',
    4: 'RealmPresetChanged',
};

export function cloneAuthPresetForRealm(
    params: CloneAuthPresetForRealmRequest
): _RequestEnvelope<CloneAuthPresetForRealmResponse> {
    return {...params, _endpoint: 'admin/authConfig/cloneAuthPresetForRealm', _resultCodeMap: _cloneAuthPresetForRealmMap};
}

// ---------------------------------------------------------------------------
// CreateAuthPreset
// ---------------------------------------------------------------------------

export interface CreateAuthPresetRequest {
    Name: string;
    Description: string;
    IsPublic: boolean;
}

export interface CreateAuthPresetResponse {
    AuthPreset: AuthPreset | null;
}

export function createAuthPreset(params: CreateAuthPresetRequest): _RequestEnvelope<CreateAuthPresetResponse> {
    return {...params, _endpoint: 'admin/authConfig/createAuthPreset', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// DeleteAuthPreset
// ---------------------------------------------------------------------------

export interface DeleteAuthPresetRequest {
    AuthPresetId: string;
}

export const DeleteAuthPresetResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    AlreadyDeleted: 'AlreadyDeleted',
} as const;
export type DeleteAuthPresetResult =
    typeof DeleteAuthPresetResultCode[keyof typeof DeleteAuthPresetResultCode];

export type DeleteAuthPresetResponse =
    {
        Result: DeleteAuthPresetResult;
    ResultDescription: string;
};

const _deleteAuthPresetMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'AlreadyDeleted',
};

export function deleteAuthPreset(
    params: DeleteAuthPresetRequest
): _RequestEnvelope<DeleteAuthPresetResponse> {
    return {...params, _endpoint: 'admin/authConfig/deleteAuthPreset', _resultCodeMap: _deleteAuthPresetMap};
}

// ---------------------------------------------------------------------------
// DisableAuthMethod
// ---------------------------------------------------------------------------

export interface DisableAuthMethodRequest {
    AuthPresetId: string;
    Type: AuthMethodType;
}

export const DisableAuthMethodResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AuthPresetNotFound: 'AuthPresetNotFound',
    AuthPresetDeleted: 'AuthPresetDeleted',
    MethodNotFound: 'MethodNotFound',
} as const;
export type DisableAuthMethodResult =
    typeof DisableAuthMethodResultCode[keyof typeof DisableAuthMethodResultCode];

export type DisableAuthMethodResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound' | 'AuthPresetDeleted' | 'MethodNotFound';
    ResultDescription: string;
    AuthPreset: null;
};

const _disableAuthMethodMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AuthPresetNotFound',
    3: 'AuthPresetDeleted',
    4: 'MethodNotFound',
};

export function disableAuthMethod(
    params: DisableAuthMethodRequest
): _RequestEnvelope<DisableAuthMethodResponse> {
    return {...params, _endpoint: 'admin/authConfig/disableAuthMethod', _resultCodeMap: _disableAuthMethodMap};
}

// ---------------------------------------------------------------------------
// FixAuthPreset
// ---------------------------------------------------------------------------

export interface FixAuthPresetRequest {
    AuthPresetId: string;
}

export const FixAuthPresetResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    PreparationFailed: 'PreparationFailed',
    AuthPresetNotFound: 'AuthPresetNotFound',
} as const;
export type FixAuthPresetResult =
    typeof FixAuthPresetResultCode[keyof typeof FixAuthPresetResultCode];

export type FixAuthPresetResponse =
    | {
    Result: 'PreparationFailed';
    ResultDescription: string;
    PreparationErrors: AuthMethodPreparationError[];
}
    | {
    Result: 'Unknown' | 'Ok' | 'AuthPresetNotFound';
    ResultDescription: string;
    PreparationErrors: null;
};

const _fixAuthPresetMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'PreparationFailed',
    3: 'AuthPresetNotFound',
};

export function fixAuthPreset(
    params: FixAuthPresetRequest
): _RequestEnvelope<FixAuthPresetResponse> {
    return {...params, _endpoint: 'admin/authConfig/fixAuthPreset', _resultCodeMap: _fixAuthPresetMap};
}

// ---------------------------------------------------------------------------
// FixRealmAuth
// ---------------------------------------------------------------------------

export interface FixRealmAuthRequest {
    RealmId: string;
}

export const FixRealmAuthResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    PreparationFailed: 'PreparationFailed',
    NoAuthPreset: 'NoAuthPreset',
    RealmNotFound: 'RealmNotFound',
} as const;
export type FixRealmAuthResult =
    typeof FixRealmAuthResultCode[keyof typeof FixRealmAuthResultCode];

export type FixRealmAuthResponse =
    | {
    Result: 'PreparationFailed';
    ResultDescription: string;
    PreparationErrors: AuthMethodPreparationError[];
}
    | {
    Result: 'Unknown' | 'Ok' | 'NoAuthPreset' | 'RealmNotFound';
    ResultDescription: string;
    PreparationErrors: null;
};

const _fixRealmAuthMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'PreparationFailed',
    3: 'NoAuthPreset',
    4: 'RealmNotFound',
};

export function fixRealmAuth(
    params: FixRealmAuthRequest
): _RequestEnvelope<FixRealmAuthResponse> {
    return {...params, _endpoint: 'admin/authConfig/fixRealmAuth', _resultCodeMap: _fixRealmAuthMap};
}

// ---------------------------------------------------------------------------
// GetAuthPreset
// ---------------------------------------------------------------------------

export interface GetAuthPresetRequest {
    AuthPresetId: string;
}

export const GetAuthPresetResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetAuthPresetResult =
    typeof GetAuthPresetResultCode[keyof typeof GetAuthPresetResultCode];

export type GetAuthPresetResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    AuthPreset: null;
};

const _getAuthPresetMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getAuthPreset(
    params: GetAuthPresetRequest
): _RequestEnvelope<GetAuthPresetResponse> {
    return {...params, _endpoint: 'admin/authConfig/getAuthPreset', _resultCodeMap: _getAuthPresetMap};
}

// ---------------------------------------------------------------------------
// ListAuthPresets
// ---------------------------------------------------------------------------

export interface ListAuthPresetsRequest {
    OnlyPublic: boolean;
    ExcludeDeleted: boolean;
}

export interface ListAuthPresetsResponse {
    AuthPresets: AuthPreset[];
}

export function listAuthPresets(params: ListAuthPresetsRequest): _RequestEnvelope<ListAuthPresetsResponse> {
    return {...params, _endpoint: 'admin/authConfig/listAuthPresets', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListRealmsForAuthPreset
// ---------------------------------------------------------------------------

export interface ListRealmsForAuthPresetRequest {
    AuthPresetId: string;
    IncludeInactive: boolean;
}

export interface ListRealmsForAuthPresetResponse {
    Realms: RealmInfo[];
}

export function listRealmsForAuthPreset(params: ListRealmsForAuthPresetRequest): _RequestEnvelope<ListRealmsForAuthPresetResponse> {
    return {...params, _endpoint: 'admin/authConfig/listRealmsForAuthPreset', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// SetDirectGoogle
// ---------------------------------------------------------------------------

export interface SetDirectGoogleRequest {
    AuthPresetId: string;
    ClientId: string;
    IsEnabled: boolean;
}

export const SetDirectGoogleResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AuthPresetNotFound: 'AuthPresetNotFound',
    AuthPresetDeleted: 'AuthPresetDeleted',
    MethodPolicyError: 'MethodPolicyError',
} as const;
export type SetDirectGoogleResult =
    typeof SetDirectGoogleResultCode[keyof typeof SetDirectGoogleResultCode];

export type SetDirectGoogleResponse =
    | {
    Result: 'MethodPolicyError' | 'Ok';
    ResultDescription: string;
    MethodPolicyError: string;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound' | 'AuthPresetDeleted';
    ResultDescription: string;
    MethodPolicyError: null;
    AuthPreset: null;
};

const _setDirectGoogleMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AuthPresetNotFound',
    3: 'AuthPresetDeleted',
    4: 'MethodPolicyError',
};

export function setDirectGoogle(
    params: SetDirectGoogleRequest
): _RequestEnvelope<SetDirectGoogleResponse> {
    return {...params, _endpoint: 'admin/authConfig/setDirectGoogle', _resultCodeMap: _setDirectGoogleMap};
}

// ---------------------------------------------------------------------------
// SetEmailWithPassword
// ---------------------------------------------------------------------------

export interface SetEmailWithPasswordRequest {
    AuthPresetId: string;
    IsEnabled: boolean;
}

export const SetEmailWithPasswordResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AuthPresetNotFound: 'AuthPresetNotFound',
    AuthPresetDeleted: 'AuthPresetDeleted',
    MethodPolicyError: 'MethodPolicyError',
} as const;
export type SetEmailWithPasswordResult =
    typeof SetEmailWithPasswordResultCode[keyof typeof SetEmailWithPasswordResultCode];

export type SetEmailWithPasswordResponse =
    | {
    Result: 'MethodPolicyError' | 'Ok';
    ResultDescription: string;
    MethodPolicyError: string;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound' | 'AuthPresetDeleted';
    ResultDescription: string;
    MethodPolicyError: null;
    AuthPreset: null;
};

const _setEmailWithPasswordMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AuthPresetNotFound',
    3: 'AuthPresetDeleted',
    4: 'MethodPolicyError',
};

export function setEmailWithPassword(
    params: SetEmailWithPasswordRequest
): _RequestEnvelope<SetEmailWithPasswordResponse> {
    return {...params, _endpoint: 'admin/authConfig/setEmailWithPassword', _resultCodeMap: _setEmailWithPasswordMap};
}

// ---------------------------------------------------------------------------
// SetGoogleViaFirebaseExternal
// ---------------------------------------------------------------------------

export interface SetGoogleViaFirebaseExternalRequest {
    AuthPresetId: string;
    ProjectId: string;
    ApiKey: string;
    AuthDomain: string;
    AppId: string;
    IsEnabled: boolean;
}

export const SetGoogleViaFirebaseExternalResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AuthPresetNotFound: 'AuthPresetNotFound',
    AuthPresetDeleted: 'AuthPresetDeleted',
    MethodPolicyError: 'MethodPolicyError',
} as const;
export type SetGoogleViaFirebaseExternalResult =
    typeof SetGoogleViaFirebaseExternalResultCode[keyof typeof SetGoogleViaFirebaseExternalResultCode];

export type SetGoogleViaFirebaseExternalResponse =
    | {
    Result: 'MethodPolicyError' | 'Ok';
    ResultDescription: string;
    MethodPolicyError: string;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound' | 'AuthPresetDeleted';
    ResultDescription: string;
    MethodPolicyError: null;
    AuthPreset: null;
};

const _setGoogleViaFirebaseExternalMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AuthPresetNotFound',
    3: 'AuthPresetDeleted',
    4: 'MethodPolicyError',
};

export function setGoogleViaFirebaseExternal(
    params: SetGoogleViaFirebaseExternalRequest
): _RequestEnvelope<SetGoogleViaFirebaseExternalResponse> {
    return {...params, _endpoint: 'admin/authConfig/setGoogleViaFirebaseExternal', _resultCodeMap: _setGoogleViaFirebaseExternalMap};
}

// ---------------------------------------------------------------------------
// SetGoogleViaFirebaseManaged
// ---------------------------------------------------------------------------

export interface SetGoogleViaFirebaseManagedRequest {
    AuthPresetId: string;
    ProjectId: string;
    AppId: string;
    ServiceAccountKeyJson: string;
    IsEnabled: boolean;
}

export const SetGoogleViaFirebaseManagedResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotReady: 'NotReady',
    AuthPresetNotFound: 'AuthPresetNotFound',
    AuthPresetDeleted: 'AuthPresetDeleted',
    MethodPolicyError: 'MethodPolicyError',
    WebAppNotFound: 'WebAppNotFound',
    PresetChanged: 'PresetChanged',
    FirebaseError: 'FirebaseError',
} as const;
export type SetGoogleViaFirebaseManagedResult =
    typeof SetGoogleViaFirebaseManagedResultCode[keyof typeof SetGoogleViaFirebaseManagedResultCode];

export type SetGoogleViaFirebaseManagedResponse =
    | {
    Result: 'NotReady' | 'MethodPolicyError' | 'FirebaseError' | 'Ok';
    ResultDescription: string;
    NotReady: GoogleViaFirebaseManagedNotReady;
    MethodPolicyError: string;
    FirebaseError: FirebaseError;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound' | 'AuthPresetDeleted' | 'WebAppNotFound' | 'PresetChanged';
    ResultDescription: string;
    NotReady: null;
    MethodPolicyError: null;
    FirebaseError: null;
    AuthPreset: null;
};

const _setGoogleViaFirebaseManagedMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotReady',
    3: 'AuthPresetNotFound',
    4: 'AuthPresetDeleted',
    5: 'MethodPolicyError',
    6: 'WebAppNotFound',
    7: 'PresetChanged',
    8: 'FirebaseError',
};

export function setGoogleViaFirebaseManaged(
    params: SetGoogleViaFirebaseManagedRequest
): _RequestEnvelope<SetGoogleViaFirebaseManagedResponse> {
    return {...params, _endpoint: 'admin/authConfig/setGoogleViaFirebaseManaged', _resultCodeMap: _setGoogleViaFirebaseManagedMap};
}

// ---------------------------------------------------------------------------
// UpdateAuthPreset
// ---------------------------------------------------------------------------

export interface UpdateAuthPresetRequest {
    AuthPresetId: string;
    Name: string | null;
    Description: string | null;
    IsPublic: boolean | null;
}

export const UpdateAuthPresetResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AuthPresetNotFound: 'AuthPresetNotFound',
    AuthPresetDeleted: 'AuthPresetDeleted',
} as const;
export type UpdateAuthPresetResult =
    typeof UpdateAuthPresetResultCode[keyof typeof UpdateAuthPresetResultCode];

export type UpdateAuthPresetResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    AuthPreset: AuthPreset;
}
    | {
    Result: 'Unknown' | 'AuthPresetNotFound' | 'AuthPresetDeleted';
    ResultDescription: string;
    AuthPreset: null;
};

const _updateAuthPresetMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AuthPresetNotFound',
    3: 'AuthPresetDeleted',
};

export function updateAuthPreset(
    params: UpdateAuthPresetRequest
): _RequestEnvelope<UpdateAuthPresetResponse> {
    return {...params, _endpoint: 'admin/authConfig/updateAuthPreset', _resultCodeMap: _updateAuthPresetMap};
}
